import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from 'reduxStore/auth/slice';
import { defaultSettingsActions } from 'reduxStore/defaultSettings/slice';
import { organisationActions } from 'reduxStore/organisations/slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { DarkLoader } from 'zComponents/atoms';
import BootErrorActions from 'zComponents/molecules/BootErrorActions';

const LOADING_STEPS = [
	{ key: 'profile', label: 'Profile' },
	{ key: 'workspace', label: 'Workspace' },
	{ key: 'orgDefaults', label: 'Default Settings' },
	{ key: 'personDefaults', label: 'Person Defaults' },
	{ key: 'orgSettings', label: 'Settings' },
	{ key: 'personSettings', label: 'Preferences' }
];

const UserLoading = () => {
	const dispatch = useDispatch();
	const {
		loginLoading,
		getProfileLoading,
		error: authError,
		profileLoadingComplete
	} = useSelector((state) => state.Auth);
	const {
		organisationLoading,
		error: workspaceError,
		organisationLoadingComplete
	} = useSelector((state) => state.Organisations);
	const {
		orgSettingsLoading,
		personSettingsLoading,
		error: settingsError,
		orgSettingsLoadingComplete,
		personSettingsLoadingComplete
	} = useSelector((state) => state.Settings);
	const {
		personDefaultSettingsLoading,
		orgDefaultSettingsLoading,
		orgDefaultSettingsLoadingComplete,
		personDefaultSettingsLoadingComplete,
		error: defaultSettingsError
	} = useSelector((state) => state.DefaultSettings);

	const [errorState, setErrorState] = useState('');

	const completionMap = useMemo(
		() => ({
			profile: profileLoadingComplete,
			workspace: organisationLoadingComplete,
			orgSettings: orgSettingsLoadingComplete,
			personSettings: personSettingsLoadingComplete,
			orgDefaults: orgDefaultSettingsLoadingComplete,
			personDefaults: personDefaultSettingsLoadingComplete
		}),
		[
			profileLoadingComplete,
			organisationLoadingComplete,
			orgSettingsLoadingComplete,
			personSettingsLoadingComplete,
			orgDefaultSettingsLoadingComplete,
			personDefaultSettingsLoadingComplete
		]
	);

	// Staggered visual completion
	const [visibleDone, setVisibleDone] = useState(new Set());
	const queueRef = useRef([]);
	const timerRef = useRef(null);

	useEffect(() => {
		LOADING_STEPS.forEach((s) => {
			if (completionMap[s.key] && !visibleDone.has(s.key) && !queueRef.current.includes(s.key)) {
				queueRef.current.push(s.key);
			}
		});

		const flush = () => {
			if (queueRef.current.length === 0) {
				timerRef.current = null;
				return;
			}
			const next = queueRef.current.shift();
			setVisibleDone((prev) => new Set(prev).add(next));
			timerRef.current = setTimeout(flush, 400);
		};

		if (!timerRef.current && queueRef.current.length > 0) {
			timerRef.current = setTimeout(flush, 200);
		}
	}, [completionMap, visibleDone]);

	useEffect(() => () => clearTimeout(timerRef.current), []);

	// Timeout warning
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (
				loginLoading ||
				getProfileLoading ||
				organisationLoading ||
				orgSettingsLoading ||
				personSettingsLoading ||
				personDefaultSettingsLoading ||
				orgDefaultSettingsLoading
			) {
				setErrorState('Loading is taking longer than expected. Please check your internet connection.');
			}
		}, 15000);
		return () => clearTimeout(timeout);
	}, [
		loginLoading,
		getProfileLoading,
		organisationLoading,
		orgSettingsLoading,
		personSettingsLoading,
		personDefaultSettingsLoading,
		orgDefaultSettingsLoading
	]);

	// Retry after 30s
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!profileLoadingComplete) dispatch(authActions.getUserProfile());
			if (!organisationLoadingComplete) dispatch(organisationActions.loadOrganisations());
			if (!orgSettingsLoadingComplete) dispatch(settingsActions.loadOrgSettings());
			if (!personSettingsLoadingComplete) dispatch(settingsActions.loadPersonSettings());
			if (!personDefaultSettingsLoadingComplete) dispatch(defaultSettingsActions.loadPersonDefaultSettings());
			if (!orgDefaultSettingsLoadingComplete) dispatch(defaultSettingsActions.loadOrgDefaultSettings());
		}, 30000);
		return () => clearTimeout(timeout);
	}, [
		profileLoadingComplete,
		organisationLoadingComplete,
		orgSettingsLoadingComplete,
		personSettingsLoadingComplete,
		personDefaultSettingsLoadingComplete,
		orgDefaultSettingsLoadingComplete,
		dispatch
	]);

	// Error
	useEffect(() => {
		const firstError = authError || workspaceError || settingsError || defaultSettingsError || '';
		if (firstError) setErrorState(firstError);
		else setErrorState('');
	}, [authError, workspaceError, settingsError, defaultSettingsError]);

	// Current step message
	const pending = LOADING_STEPS.find((s) => !visibleDone.has(s.key));
	const currentMessage = (() => {
		if (errorState) return errorState;
		if (pending) return `Loading ${pending.label}...`;
		return 'Almost ready...';
	})();

	// An error can outlive every retry; never leave the user without a way out.
	return <DarkLoader message={currentMessage}>{errorState && <BootErrorActions />}</DarkLoader>;
};

export default UserLoading;
